import type { FC } from "react";
import { useCallback } from "react";

import classNames from "classnames";
import { A11y, Autoplay, Navigation, Pagination } from "swiper/modules";
import type { SwiperProps } from "swiper/react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper/types";

import { Loader } from "@jti/core-components/components/Loader";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import styles from "./SwiperJti.module.scss";

export { SwiperSlide };

export const SWIPE_THRESHOLD = 10;

export type SwiperJtiProps = SwiperProps & {
  mode?: "dark" | "light";
  rtl?: boolean;
  swiperJtiClass?: string;
  swiperJtiWrapper?: string;
  isShowNavigationArrows?: boolean;
  isShowPaginationButtons?: boolean;
  isContainerWidth?: boolean;
  withoutSlidesMinHeight?: boolean;
  isViewportHeight?: boolean;
  isCustomPagination?: boolean;
  handleSlideChange?: (swiper: SwiperClass) => void;
  showLoader?: boolean;
  isOverflowEnabled?: boolean;
  isAutoplayEnabled?: boolean;
  autoplayDelay?: number;
  noBorderRadiusOnMobile?: boolean;
};

export const SwiperJti: FC<SwiperJtiProps> = ({
  children,
  mode = "light",
  rtl,
  swiperJtiClass,
  swiperJtiWrapper,
  isShowNavigationArrows = true,
  isShowPaginationButtons = true,
  isContainerWidth = false,
  withoutSlidesMinHeight,
  isViewportHeight = true,
  isCustomPagination = false,
  handleSlideChange,
  showLoader = false,
  isOverflowEnabled = false,
  isAutoplayEnabled = false,
  autoplayDelay = 5000,
  slidesPerView = 1,
  noBorderRadiusOnMobile = false,
  ...restProps
}) => {
  const {
    autoHeight = false,
    className: customSwiperClass,
    onClick: userOnClick,
    onTouchEnd: userOnTouchEnd,
    onNavigationNext: userOnNavigationNext,
    onNavigationPrev: userOnNavigationPrev,
    onSlideChange: userOnSlideChange,
    ...swiperProps
  } = restProps;

  const swiperWrapperClasses = classNames(styles.swiperJtiWrapper, swiperJtiWrapper, {
    [styles.swiperJtiWrapperDark]: mode === "dark",
    [styles.swiperJtiWrapperRtl]: rtl,
    [styles.swiperJtiContainerWidth]: isContainerWidth,
    [styles.swiperJtiViewPortHeight]: !autoHeight && isViewportHeight,
    [styles.swiperJtiCustomPagination]: isCustomPagination,
    [styles.swiperJtiNotViewPortHeight]: !autoHeight && !isViewportHeight && !withoutSlidesMinHeight,
    [styles.swiperJtiWithLoader]: showLoader,
    [styles.swiperJtiOverflowVisible]: isOverflowEnabled,
    [styles.swiperJtiNoBorderRadiusOnMobile]: noBorderRadiusOnMobile,
  });

  const dispatchTracking = useCallback(() => {
    dispatchTrackingEvent(TrackingEvents.TrackingCarouselClick);
  }, []);

  const handleNavigationNext = useCallback(
    (swiper: SwiperClass) => {
      dispatchTracking();
      userOnNavigationNext?.(swiper);
    },
    [dispatchTracking, userOnNavigationNext]
  );

  const handleNavigationPrev = useCallback(
    (swiper: SwiperClass) => {
      dispatchTracking();
      userOnNavigationPrev?.(swiper);
    },
    [dispatchTracking, userOnNavigationPrev]
  );

  const handleClick = useCallback(
    (swiper: SwiperClass, event: MouseEvent | TouchEvent | PointerEvent) => {
      // only dispatch event on pagination bullet click
      const target = event.target as HTMLElement;
      if (target.closest(".swiper-pagination-bullet")) {
        dispatchTracking();
      }
      userOnClick?.(swiper, event);
    },
    [dispatchTracking, userOnClick]
  );

  const handleTouchEnd = useCallback(
    (swiper: SwiperClass, event: MouseEvent | TouchEvent | PointerEvent) => {
      // only dispatch event on actual swipe
      if (Math.abs(swiper.touches.diff) > SWIPE_THRESHOLD) {
        dispatchTracking();
      }
      userOnTouchEnd?.(swiper, event);
    },
    [dispatchTracking, userOnTouchEnd]
  );

  const handleSlideChangeInternal = useCallback(
    (swiper: SwiperClass) => {
      handleSlideChange?.(swiper);
      userOnSlideChange?.(swiper);
    },
    [handleSlideChange, userOnSlideChange]
  );

  const mergedSwiperClassName = classNames(swiperJtiClass, customSwiperClass);

  return (
    <div className={swiperWrapperClasses} dir={rtl ? "rtl" : ""} data-testid="swiper-jti">
      {showLoader && (
        <div className={classNames("hero-loader", styles.loader)}>
          <Loader />
        </div>
      )}
      <Swiper
        {...swiperProps}
        className={mergedSwiperClassName}
        modules={[Navigation, Pagination, A11y, Autoplay]}
        pagination={isShowPaginationButtons ? { clickable: true } : false}
        navigation={isShowNavigationArrows}
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        onNavigationNext={handleNavigationNext}
        onNavigationPrev={handleNavigationPrev}
        touchStartPreventDefault={false}
        onSlideChange={handleSlideChangeInternal}
        autoHeight={autoHeight}
        slidesPerView={slidesPerView}
        autoplay={isAutoplayEnabled ? { delay: autoplayDelay, disableOnInteraction: false } : false}
      >
        {children}
      </Swiper>
    </div>
  );
};
